import * as React from "react";
import { Control, Path, useController, useWatch } from "react-hook-form";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import { type SxProps } from "@mui/material/styles";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { isValid } from "date-fns";

import CheckboxFormField from "components/checkbox-form-field";
import { NON_BREAKABLE_SPACE } from "constants/common";

type BirthdayFieldProps<T = any> = {
  control: Control<T>;
  notProvidedName: Path<T>;
  showIcon?: boolean;
  onCheckboxClick?: (checked: boolean) => void;
  containerSx?: SxProps;
  checkboxSx?: SxProps;
  disabled?: boolean;
};

export default function BirthdateField<T = any>({
  control,
  notProvidedName,
  showIcon = true,
  onCheckboxClick,
  containerSx,
  checkboxSx,
  disabled = false
}: BirthdayFieldProps<T>) {
  const notProvidedCheckboxValue = useWatch({
    control,
    name: notProvidedName
  });

  const {
    field,
    formState: { errors }
  } = useController({
    name: "birthdate",
    control,
    rules: {
      required: notProvidedCheckboxValue
        ? undefined
        : "Date of Birth is required",
      validate: (value) => {
        if (notProvidedCheckboxValue) return true;
        return isValid(value) || "Date of Birth is not valid.";
      }
    }
  });

  return (
    <Stack sx={containerSx}>
      <Box display="flex">
        {showIcon && (
          <CalendarMonthOutlinedIcon
            sx={{ mt: "30px", mr: 1.5 }}
            color="action"
          />
        )}

        <Stack flexDirection="row" sx={{ width: "100%" }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              {...field}
              views={["year", "month", "day"]}
              value={field.value ? new Date(field.value) : null}
              label="Date of Birth"
              disabled={notProvidedCheckboxValue || disabled}
              slotProps={
                {
                  textField: {
                    required: !notProvidedCheckboxValue,
                    ...(Boolean(errors?.birthdate?.message)
                      ? {
                          error: true,
                          helperText: errors?.birthdate?.message
                        }
                      : {}),
                    readOnly: notProvidedCheckboxValue,
                    fullWidth: true
                  }
                } as any
              }
            />
          </LocalizationProvider>
        </Stack>
      </Box>

      <FormControlLabel
        label={`Not${NON_BREAKABLE_SPACE}Provided`}
        disabled={disabled}
        control={
          <CheckboxFormField
            name={notProvidedName}
            control={control}
            testId="birth-date-not-provided"
            sx={{ ml: 5, ...checkboxSx }}
            onClick={onCheckboxClick}
          />
        }
      />
    </Stack>
  );
}
